// ABOUTME: Authored-pixel geometry for the reel chrome, taken from Figma node 45:4838 / 45:2318.
// ABOUTME: Everything is expressed in the 3840x2160 desktop space and scaled at render by `--u`.

// SPEC-26 Phase 4 Revision. The desktop frames are authored at 3840x2160. Rather than converting
// every value to a percentage by hand — which is what drifted last pass — the chrome declares one
// CSS custom property `--u` ("one authored pixel, in rendered pixels") and every dimension below is
// used as `calc(<authored> * var(--u))`. At the 3840px authored width `--u` is UI_SCALE; below
// 2064px it sits on its 0.43px floor, so an 88px chip renders at 37.8px on every laptop and 1080p
// monitor: the design's own proportions, at a fixed scale.
//
// The floor stops the chrome collapsing to unreadable sizes on a small laptop; the ceiling stops it
// growing past its authored size on a 5K display. 1/3840 = 0.0260417vw.
//
// UI_SCALE (2026-08-27, team review): the chrome was a straight 1:1 of the Figma frame, and at that
// scale it reads as oversized on an ordinary monitor — the Figma composition is authored for a
// 3840-wide cinema frame, so "correct" and "comfortable" are not the same number here. Every
// authored value stays as Figma drew it; only the unit shrinks, so all proportions are preserved.
// This is the single knob for the overall base scale of the desktop chrome — change it, not the
// authored values. Keep it in step with mobileGeometry.js's UI_SCALE.
export const UI_SCALE = 0.80;

// Derived from UI_SCALE rather than written out, so the constant above is genuinely the only edit
// needed to retune the base scale. The FLOOR is deliberately NOT scaled: it is a legibility floor,
// not a scale. At UI_SCALE 0.80 it binds below 2064px (0.43 / 0.0208333vw) — every laptop and every
// 1080p monitor, which therefore all render the chrome at one fixed size; the "~1795px" earlier
// notes quoted was the UI_SCALE 0.92 figure. Scaling the floor too would set 26.667px body copy at
// 10.6 real px there, under the 11px floor the mobile chrome holds itself to.
const RAIL_SLOPE_VW = 100 / 3840;   // 0.0260417 — one authored px as a fraction of the frame
export const RAIL_UNIT_FLOOR_PX = 0.43;

export const RAIL_UNIT =
  `clamp(${RAIL_UNIT_FLOOR_PX}px, ${(RAIL_SLOPE_VW * UI_SCALE).toFixed(7)}vw, ${UI_SCALE}px)`;

// Caption bar unit (2026-08-28): smaller ceiling (0.70) than the chrome (0.80). NOTE what this
// actually does: CaptionBar sets it as the bar's `--u`, so EVERYTHING inside the bar — the caption
// type included — is scaled by it, and the bar's padding/radius are reduced a second time by
// CaptionOverlay's BAR_OUTER_SCALE (0.875). Both floors are 0.43, so below 2359px wide the two units
// are identical and only the padding factor is visible; above it the caption type runs 12.5%
// smaller than the chrome's other 37.333 tokens. Recorded rather than changed (2026-09-03).
const CAPTION_BAR_SCALE = 0.70;
export const CAPTION_BAR_UNIT =
  `clamp(${RAIL_UNIT_FLOOR_PX}px, ${(RAIL_SLOPE_VW * CAPTION_BAR_SCALE).toFixed(7)}vw, ${CAPTION_BAR_SCALE}px)`;

/** @param {number} authoredPx a length measured in the Figma 3840x2160 frame */
export const u = (authoredPx) => `calc(${authoredPx} * var(--u))`;

/**
 * The same, with a hard floor in real pixels — the desktop twin of `mobileGeometry.muMin`, for the
 * handful of elements that are too small to survive the unit's floor. CSS `max()` rather than a JS
 * clamp because `--u` is only known to the browser.
 */
export const uMin = (authoredPx, minPx) => `max(${minPx}px, calc(${authoredPx} * var(--u)))`;

/**
 * The item-count badge on a collapsed rail chip, and the ×N pill on a multi-quantity tile.
 *
 * Not authored in Figma — the mockups never draw a non-empty bag on a collapsed chip — so these are
 * chosen against the chip rather than measured: 44 is exactly half the 88 chip, which is the
 * proportion the badge held at the sizes it was originally hardcoded to (20px on a ~0.46 unit).
 * Expressed in authored px anyway, because the alternative is what these were before: a fixed-px
 * island on a surface that scales, which drifts visibly the moment UI_SCALE is retuned.
 *
 * The floors are what a fixed size was implicitly buying — a count has to stay readable at the
 * unit's floor, where 44 authored px would otherwise set at 18.9 and its type at 10.3.
 */
export const CHIP_BADGE = {
  size: 44,      // authored diameter, half the 88 chip
  font: 24,      // authored type size
  minSize: 18,   // real-px floor for the disc
  minFont: 10,   // real-px floor for the digits — matches the old hardcoded text-[10px]
  padX: 8,       // authored horizontal padding, so a 2-3 digit count widens rather than clips
};

// Rail container — node 45:4838. Offsets are viewport percentages, not `--u` multiples, so the
// chrome hugs the corner at any aspect ratio instead of drifting when the video box is letterboxed.
export const RAIL_LEFT_PCT = (106.664 / 3840) * 100;  // 2.778%
export const RAIL_TOP_PCT = (106.667 / 2160) * 100;   // 4.938%

// The Figma frame is authored for an edge-to-edge fullscreen view. In an actual browser tab, the
// same percentage leaves the top chrome sitting almost flush against the browser's own UI (tabs,
// bookmarks bar). This fixed pixel addition — on top of the authored percentage, not instead of it
// — gives it clearance without meaningfully changing the design's proportions on a real screen.
export const CHROME_TOP_CLEARANCE_PX = 14;
export const topOffset = (pct) => `calc(${pct}% + ${CHROME_TOP_CLEARANCE_PX}px)`;

// Collapsed accordion header — 88px chip on a 101.33px pitch, i.e. a 13.33px gap.
export const CHIP = 88;
export const HEADER_PITCH = 101.333;
export const STACK_GAP = HEADER_PITCH - CHIP; // 13.333

// Expanded panel — node 45:2319 ("Frame 185"), the frosted rect that actually renders.
// The content column was widened past its authored 877.333 (client feedback, 2026-08-24): the
// Figma-authored "See full …" escalation to a full-height drawer was rejected in favour of showing
// every item in place, so the panel needed a little more room of its own rather than borrowing the
// drawer's. Padding stays symmetrical around it.
export const PANEL_PAD = 53.333;
export const PANEL_CONTENT_W = 930;
export const PANEL_W = PANEL_CONTENT_W + PANEL_PAD * 2; // 1036.667
export const PANEL_RADIUS = 40; // reel.radius.panel

/**
 * The panel's width, capped so it can never run off a narrow viewport.
 *
 * The `--u` floor (0.43) exists to keep the chrome legible, and below 2064px it stops tracking the
 * viewport at all — so on a genuinely narrow screen the authored 1036.667 resolves to ~446 real px
 * regardless of how little room there is. That is fine on the narrowest desktop *window* anyone
 * drags (480px at 400% zoom), and it overflows by ~67px on the one case that goes narrower: a phone
 * with "Request Desktop Site" on, which drops `mobileUA` and therefore gets the desktop composition
 * at ~390px. See the matrix row of the same name in responsiveMatrix.test.js.
 *
 * A cap rather than a lower floor, because the floor is doing a different job: this leaves every
 * viewport that has the room rendering at exactly the authored size, and only bites where the
 * alternative is horizontal overflow. The rail is anchored at RAIL_LEFT_PCT from the left, so the
 * room available is the viewport minus that inset twice — matching it on the right keeps the panel
 * optically centred in what is left rather than jammed against the edge.
 */
export const PANEL_MAX_VW = 100 - RAIL_LEFT_PCT * 2; // 94.444vw

/** CSS for the above. `panelWidthFor` is the same value in JS, for tests and arithmetic. */
export const panelWidth = () => `min(${u(PANEL_W)}, ${PANEL_MAX_VW}vw)`;

/** @param {number} viewportWidth @param {number} unit resolved `--u`, in real px */
export function panelWidthFor(viewportWidth, unit) {
  return Math.min(PANEL_W * unit, (PANEL_MAX_VW / 100) * viewportWidth);
}

// Desktop CC bar geometry (mirrors CaptionOverlay.js's BAR_BOTTOM_PCT/BAR_H_PCT — duplicated rather
// than imported, to avoid a railGeometry <-> CaptionOverlay import cycle; CaptionOverlay.js already
// imports FROM this file). Both live in the same 3840x2160 authored frame as the rail itself.
const BAR_BOTTOM_AUTHORED = 96;  // 2160 - 2064
const BAR_H_AUTHORED = 192;
const PANEL_TOP_AUTHORED = (RAIL_TOP_PCT / 100) * 2160; // 106.667, the frame this % was derived from
// Breathing room above the bar, on top of the panel's own frosted padding — same order of magnitude
// as PANEL.sectionGap below.
const PANEL_FOOTER_GUTTER_AUTHORED = 40;
// The rail is a 3-item accordion (bag, favourites, privacy) and only one expands at a time — the two
// that stay collapsed still sit ABOVE whichever panel is open when it isn't the first item. Reserving
// both unconditionally (worst case: privacy, the third item, is open) keeps one ceiling correct for
// all three rather than needing to know which is open.
const PANEL_STACK_RESERVE_AUTHORED = 2 * CHIP + 2 * STACK_GAP; // 202.667
const PANEL_MAX_H_AUTHORED = 2160 - PANEL_TOP_AUTHORED - BAR_BOTTOM_AUTHORED - BAR_H_AUTHORED
  - PANEL_FOOTER_GUTTER_AUTHORED - PANEL_STACK_RESERVE_AUTHORED; // 1522.667

/**
 * Ceiling on an expanded rail panel's (cart/wishlist/privacy) height so its frosted edge never
 * reaches the CC bar — reported 2026-08-28, the privacy panel's CANCEL/SAVE row slightly overlapping
 * the bar on a shorter viewport. Derived entirely in the rail's own authored frame (the one `u()`
 * already scales), so this one subtraction holds at every screen size rather than needing a second,
 * viewport-unit-based formula to keep in step with it. `overflow-y: auto` is the fallback for the
 * cart/wishlist panels, whose content is genuinely unbounded (a guest's bag can hold anything); the
 * privacy panel additionally gets a measured, never-scrolls fit — see `ConsentPanel.js`'s own
 * `useLayoutEffect`, and `railUnitFor` below for the base unit it starts that measurement from.
 */
// Two ceilings, the lower wins (2026-09-03). The authored subtraction above assumes the viewport IS
// the frame at the unit's scale, 2160 × `--u` tall — which stops being true the moment the unit's
// floor binds (every viewport under 2064px wide): `--u` is then a constant while the rail top, the
// bar's bottom and the bar's height are still PERCENTAGES of the real height. At 1366x768 the
// authored ceiling allowed 641px where 510 existed above the bar, so a two-row bag panel sat 27px
// into the caption bar (66px at 1280x720) — the very overlap the ceiling was added to stop. The
// second term is the same subtraction against the real viewport height. `min()` rather than a
// replacement: wherever the room exists (1920x1080 and up) the authored term is the lower one, so
// nothing that fit before renders any differently.
const PANEL_CHROME_PCT = RAIL_TOP_PCT + ((BAR_BOTTOM_AUTHORED + BAR_H_AUTHORED) / 2160) * 100; // 18.27
const PANEL_RESERVE_AUTHORED = PANEL_FOOTER_GUTTER_AUTHORED + PANEL_STACK_RESERVE_AUTHORED;   // 242.667

export const panelMaxHeight = () => (
  `min(calc(${PANEL_MAX_H_AUTHORED} * var(--u) - ${CHROME_TOP_CLEARANCE_PX}px), `
  + `calc(var(--reel-vh, 100vh) * ${((100 - PANEL_CHROME_PCT) / 100).toFixed(6)} `
  + `- ${CHROME_TOP_CLEARANCE_PX}px - ${PANEL_RESERVE_AUTHORED} * var(--u)))`
);

/** The same ceiling as a number — for tests and arithmetic. @param {number} unit resolved `--u` */
export function panelMaxHeightFor(viewportWidth, viewportHeight, unit) {
  // Explicit fallback to avoid hoisting order issues: if unit is undefined, compute it from
  // viewportWidth, which is always provided (tests call with both width and height).
  const resolvedUnit = unit !== undefined ? unit : railUnitFor(viewportWidth);
  const authored = PANEL_MAX_H_AUTHORED * resolvedUnit - CHROME_TOP_CLEARANCE_PX;
  const viewport = viewportHeight * ((100 - PANEL_CHROME_PCT) / 100)
    - CHROME_TOP_CLEARANCE_PX - PANEL_RESERVE_AUTHORED * resolvedUnit;
  return Math.min(authored, viewport);
}

/**
 * `RAIL_UNIT`'s clamp, resolved to a number in JS — the desktop twin of `mobileGeometry.js`'s
 * `mobileUnitFor`. Needed so a caller can compute a real `--u` value (not just hand the CSS clamp()
 * string to the browser) and do arithmetic on it — specifically, `ConsentPanel.js` uses this as the
 * starting point for its own measured fit-to-viewport pass.
 *
 * @param {number} viewportWidth
 * @returns {number} `--u`, in real px
 */
export function railUnitFor(viewportWidth) {
  if (!viewportWidth) return UI_SCALE;
  return Math.min(
    Math.max((RAIL_SLOPE_VW * UI_SCALE * viewportWidth) / 100, RAIL_UNIT_FLOOR_PX), UI_SCALE,
  );
}

// Panel internals, as offsets inside Frame 185 (see SPEC-26 R.2). The "See full …" escalation and
// its "N more items" summary are gone (client feedback, 2026-08-24): asking the guest — or the
// agent — to open the same list twice made no sense once the agent could already open/close it
// directly. Everything from the grid down is flow-spaced (SECTION_GAP etc.) rather than pinned to
// the old authored offsets, because the grid's own height is now variable (all items render, not a
// fixed two).
export const PANEL = {
  glyphTop: 53.333,        // 45:2320
  titleTop: 147.218,       // 45:2323
  ruleTop: 207.885,        // 45:2324
  gridTop: 245.218,        // 45:2325
  ruleH: 2.667,
  tile: 432,               // 45:2327 — square product image, full size (<= ITEMS_PER_ROW items)
  tileGap: 13.333,         // 445.333 - 432
  nameTop: 464,            // 45:2329, relative to the tile's top
  //
  // DIVERGENCE 1 of 2 — the name column (`nameW`), RETIRED. Figma node 45:2329 authors the product
  // name in a 265-wide box inside a 432 tile, and that narrowness is what makes "OPH 411 Vesper
  // Black Dial" break onto two lines in the design. The build no longer has such a box:
  //   * 2026-09-01, client feedback: with 167 authored px of empty tile to the right of every
  //     title, real catalogue names wrapped far earlier than they needed to, and a title that
  //     wrapped beside one that didn't pushed its price and action strip a line below its
  //     neighbour's — the row read as misaligned. `nameW` went 265 -> 432 (the tile's full width).
  //   * Same day, second round of the same feedback (redlined screenshot): the tile stopped being
  //     a fixed-width island altogether — see DIVERGENCE 2 below — so the title now takes
  //     `max-width: 100%` of its grid column and the constant has no reader left. Removed rather
  //     than left dangling; this note is the provenance for 45:2329's authored 265.
  // The deliberate two-line look the 265 was buying survives both moves, via the reserved
  // NAME_BLOCK_H below (a fixed two-line box, clamped at two lines) rather than by starving the
  // title of width — which is what keeps a row aligned whether a title takes one line or two.
  //
  // DIVERGENCE 2 of 2 — the grid columns. Figma lays the panel out as two 432-wide tiles on a
  // 445.333 pitch (45:2327), and the build followed that literally: `repeat(2, <tileSize>)`. Once
  // tiles step down past ITEMS_PER_ROW items (TILE_COMPACT_SCALE, added 2026-08-24 and NOT authored
  // in Figma at all) that leaves the row hugging the panel's left edge — 2 x 345.6 + 13.333 = 704.5
  // of the 930 content column, ~225 authored px of dead gutter down the right of every row. The
  // client redlined exactly that gutter on both rows of a 5-item bag (2026-09-01) and chose equal
  // half-panel columns, so the grid is now `repeat(ITEMS_PER_ROW, 1fr)` across the full content
  // width. The tile ARTWORK is unchanged and centres in the wider column — growing it was offered
  // and turned down, because a taller row means fewer items before the grid starts scrolling.
  // Nothing vertical moves: ITEM_BLOCK_H below is a sum of heights only, so the scroll paging,
  // the chevron thresholds and the reported at_top/at_bottom are untouched by either divergence.
  priceTop: 96,            // 45:2330, relative to the name block
  heartTop: 146,           // 45:2331, relative to the name block
  heartW: 34.667,
  heartH: 31.018,
  ctaH: 68.667,
  actionGap: 20,           // gap between controls in a bag row's action strip (see ACTION below)
  sectionGap: 40,          // grid -> subtotal hairline (cart only)
  subtotalGap: 32,         // hairline -> subtotal row
  ctaGap: 24,              // subtotal row -> checkout button
  bottomPad: 53.333,
};

// Once a bag/favourites list grows past one row, tiles step down to this fraction of PANEL.tile so a
// second row fits without the panel doubling in height outright (client feedback, 2026-08-24: "fit a
// bit more by default, slightly scaled down"). Type stays at its authored size at every step —
// legibility, not layout, is the floor.
// A bag row's action strip: heart (save/unsave) · − · + · ✕. Figma authors exactly ONE control on a
// bag row — the outline heart at node 45:2331 — and no quantity affordance at all, so everything but
// the heart is a build addition (client, 2026-08-26). They are painted at the heart's authored HEIGHT
// so the strip reads as one optical line, and the row's total height is unchanged, which is why
// ITEM_BLOCK_H below still resolves off `heartH` alone.
export const ACTION_ICON = PANEL.heartH; // 31.018 — square box for the lucide stroke glyphs

export const TILE_COMPACT_SCALE = 0.8;
export const ITEMS_PER_ROW = 2;
export const ROWS_BEFORE_SCROLL = 2; // beyond this many rows, the grid scrolls instead of shrinking further
// Full vertical extent of one tile + its name/price/heart block, image top to heart bottom.
export const ITEM_BLOCK_H = PANEL.nameTop + PANEL.heartTop + PANEL.heartH; // 641.018

// Glyph artwork, sized from the real vector nodes — NOT from the layer names, which lie in this
// file (a heart lives in a layer called `Saved`, a sliders control in one called `Cookies`).
// Verified by comparing each SVG's width/height attribute to its source node. See SPEC-26 R.5.
export const GLYPHS = {
  bag: { src: '/reel-chrome/icon-bag.svg', w: 49, h: 59.218, node: '45:4857' },
  saved: { src: '/reel-chrome/icon-saved.svg', w: 63.002, h: 56.0, node: '45:4863' },
  // No filled/active heart is authored anywhere in the file — every favourites tile in 45:2395 uses
  // the same outline heart whether saved or not, so there is no visual "this is already saved" state
  // to derive. This is the authored outline's own outer contour filled in (same viewBox, same
  // silhouette), added so the toggle has a state to show. See SPEC-26 R.5 visual pass.
  savedFilled: { src: '/reel-chrome/icon-saved-filled.svg', w: 63.002, h: 56.0, node: '45:4863*' },
  privacy: { src: '/reel-chrome/icon-privacy.svg', w: 66.198, h: 55.402, node: '45:4872' },
};

// Type scale (DESIGN.md §2 "Typography"), in authored px.
export const TEXT = {
  panelTitle: 26.667,   // reel.text.button — "Shopping Bag (4)"
  productLine: 33.6,    // reel.text.productLine — product name
  price: 26.667,
  button: 26.667,
};

// Reserved height for a tile's product name (client feedback, 2026-09-01).
//
// The tiles in a row are independent blocks in a CSS grid, so a one-line title and a two-line title
// used to give their price and action strip different baselines — visibly "one tile hanging lower
// than the other". Rather than chase that with alignment tricks, the name gets a FIXED two-line box:
// an explicit line-height (so the browser's font-dependent default can't vary it), a min-height of
// exactly two of those lines, and a 2-line clamp so an unusually long catalogue name can never push
// past it and re-introduce the same misalignment from the other direction.
//
// 1.2 rather than Tailwind's `leading-tight` (1.25): it is an exact number this file can multiply,
// and the authored gap this block sits in (PANEL.priceTop = 96 from the name block's top) has room
// for 2 x 33.6 x 1.2 = 80.64 but not for 2 x 33.6 x 1.25 x plus the existing 24 margin. The type
// size itself is deliberately NOT scaled by TILE_COMPACT_SCALE (see ProductTile), so neither is this.
export const NAME_LINES = 2;
export const NAME_LINE_HEIGHT = 1.2;
export const NAME_BLOCK_H = TEXT.productLine * NAME_LINE_HEIGHT * NAME_LINES; // 80.64

// Consent/privacy panel — node 113:6517 ("Cookies Consetina v2"), the third rail accordion item.
// Unlike the cart/wishlist panel (a variable-height product grid, hence PANEL's flow-spacing), this
// content is a fixed three-row list, so it keeps Figma's own pinned gaps between sections rather than
// re-deriving them. Reuses PANEL_PAD/PANEL_CONTENT_W/PANEL_RADIUS — same frosted container as the
// other two rail panels, just with this panel's own internal offsets.
export const CONSENT_TEXT = {
  title: 49.917,     // "Privacy & Data Preferences"
  link: 37.333,      // "READ OUR PRIVACY POLICY"
  rowTitle: 37.333,  // "Voice & Audio Recording" / "ALWAYS ON" badge
  rowBody: 26.667,   // row description copy
  cta: 37.333,       // CANCEL / SAVE PREFERENCES
};
export const CONSENT_PANEL = {
  // Pinned offsets, kept for provenance (all relative to the panel's own top-left, node 113:6517):
  glyphTop: 53.333,        // 113:6519, same as PANEL.glyphTop
  titleTop: 178.667,       // 113:6527
  linkTop: 293.333,        // 113:6530
  rowsTop: 394.667,        // 113:6531 — first row card
  // Flow gaps derived from the offsets above — what ConsentPanel actually consumes, since it
  // stacks glyph -> title -> link -> rows -> footer with margin-top rather than absolute position.
  glyphToTitleGap: 69.932, // titleTop - glyphTop - GLYPHS.privacy.h
  titleToLinkGap: 64.749,  // linkTop - titleTop - CONSENT_TEXT.title
  linkToRowsGap: 64.001,   // rowsTop - linkTop - CONSENT_TEXT.link
  rowH: 205.333,           // 113:6532/6538/6544
  rowGap: 53.333,          // 653.33 - (394.67 + 205.33)
  rowRadius: 40,           // reel.radius.panel — same radius as the outer panel
  rowIconSize: 48,         // 113:6533 checkbox glyph box
  footerGap: 66.667,       // last row bottom -> CANCEL/SAVE row top
  footerH: 74.667,         // 113:6550
  footerBtnGap: 45.333,    // 538.667 - 493.333 — gap between CANCEL and SAVE PREFERENCES
};

// The connection indicator (App.js) — not authored in Figma; it was a Tailwind `text-xs md:text-sm`
// beside a 10px dot, the one piece of reel chrome that never scaled. Authored sizes are DERIVED
// from the old fixed sizes at the unit's floor (14 / 0.43, 10 / 0.43), with those sizes as the
// floors, so every viewport that rendered it before renders it pixel-identical and only a display
// wider than 2064px sees it grow with the rest of the chrome (2026-09-03).
export const INDICATOR = {
  font: 14 / RAIL_UNIT_FLOOR_PX,   // 32.558 authored
  dot: 10 / RAIL_UNIT_FLOOR_PX,    // 23.256 authored
  minFont: 14,                     // the old `md:text-sm`
  minFontMobile: 12,               // the old `text-xs`
  minDot: 10,                      // the old `w-2.5`
};

// Wordmark — node 45:1219, 258.67x141.33 at (1792, 106.67). Centred; 6.74% of width, 4.94% down.
// Client feedback (2026-08-28): the authored size read too large on the reel screen — scaled down
// 10% from the Figma export. EnterBoutiqueGate reuses this same widthPct (instead of its own fixed
// Tailwind height) so the mark renders at an identical width on both screens.
const WORDMARK_SCALE = 0.9;
export const WORDMARK = {
  widthPct: (258.667 / 3840) * 100 * WORDMARK_SCALE,
  topPct: (106.667 / 2160) * 100,
};

// ── The right-anchored panel must clear the centred chrome (owner report 2026-10-07) ─────────────
// With the rail on the right (bag_top_right / collection_menu) the wordmark and the connection indicator stay centred, so below
// ~1090px the opened bag / favourites panel — which sits on its --u floor and so stops shrinking with the window — reached them and
// the chip that opened it. The panel's width is now capped by what is left between its right anchor and the centre chrome.
// A CAP, not a retune: wherever the room exists (every laptop and wider) the existing `panelWidth()` is the smaller term and the
// rendered width is exactly what it was.

// The widest the indicator gets is "Connecting" (App.js), not "Online": measured 120.83px in Chrome at the unit's floor (14px type,
// 10px dot, 8px gap), so 60.4px each side of the centre, 61 rounded up. Authored the way INDICATOR's own sizes are — derived at the
// floor — so it grows with the type on a display wider than 2064px (measured 217.86px at 3840: 109px, under 141.9 * 0.8 = 113.5).
const INDICATOR_HALF_MIN_PX = 61;
const INDICATOR_HALF_AUTHORED = INDICATOR_HALF_MIN_PX / RAIL_UNIT_FLOOR_PX; // 141.86
// Clearance between the panel's edge and the centre chrome: two of the rail's own header gaps.
const CENTRE_CLEAR_GAP = 2 * STACK_GAP; // 26.667

// The narrowest the cap may take a panel: two tile columns that can still hold a bag row's action strip (heart · − · + · ✕) between
// the panel's padding. Below this the panel stops yielding and covers the centre chrome rather than becoming unusable.
const ACTION_STRIP = 4 * ACTION_ICON + 3 * PANEL.actionGap; // 184.07
export const PANEL_MIN_W = PANEL_PAD * 2 + ITEMS_PER_ROW * ACTION_STRIP + PANEL.tileGap; // 488.1

/** The room the wordmark / indicator need either side of the centre, as CSS: the wider of the two plus the gap. */
const centreHalfClearance = () =>
  `calc(max(${WORDMARK.widthPct / 2}vw, ${uMin(INDICATOR_HALF_AUTHORED, INDICATOR_HALF_MIN_PX)}) + ${u(CENTRE_CLEAR_GAP)})`;

/** `panelWidth()` for a panel anchored at the RIGHT rail inset: never wider than the room left of that anchor up to the centre chrome. */
export const panelWidthClearOfCentre = () =>
  `min(${panelWidth()}, max(calc(50vw - ${RAIL_LEFT_PCT}vw - ${centreHalfClearance()}), ${u(PANEL_MIN_W)}))`;

/** The same value in JS, for tests and arithmetic. @param {number} viewportWidth @param {number} unit resolved `--u`, in real px */
export function panelWidthClearOfCentreFor(viewportWidth, unit) {
  const half = Math.max((WORDMARK.widthPct / 100) * viewportWidth / 2, INDICATOR_HALF_MIN_PX, INDICATOR_HALF_AUTHORED * unit);
  const room = viewportWidth / 2 - (RAIL_LEFT_PCT / 100) * viewportWidth - half - CENTRE_CLEAR_GAP * unit;
  return Math.min(panelWidthFor(viewportWidth, unit), Math.max(room, PANEL_MIN_W * unit));
}

// ── The LEFT-anchored panel must clear the centred wordmark (owner round 2, 2026-10-07) ───────────────────────────────────────────
// The mirror of the above for the shipped (flag-off) rail, which sits at the LEFT inset: measured, its opened Shopping Bag reached the
// centred wordmark at 768x1024 and at every window narrower than ~1009px (the --u floor stops it shrinking). The room left of the
// wordmark is the same subtraction as on the right — the left anchor is the same RAIL_LEFT_PCT from its edge — so the derivation and the
// PANEL_MIN_W floor are the right-hand cap's own. Two deliberate differences:
//  * The connection indicator is NOT in the way. With the rail on the left the indicator sits in the opposite (right) corner
//    (topChromeLayout: 'corner'), so only the wordmark's half-width counts; including the indicator's 61px would have narrowed the panel
//    at widths where it reaches nothing today.
//  * The clearance is ONE rail gap (STACK_GAP), not two. This is the shipped UI and the owner's rule is that no width that does not overlap
//    today may change: at 1024x768 (iPad landscape) today's panel clears the wordmark by 6.6px, and one gap (5.7px there) is the largest
//    clearance that leaves that width exactly as it is. The cap therefore changes pixels only below ~1022px.
// A CAP, not a retune: where the room exists `panelWidth()` is the smaller term and the rendered width is exactly what it was.
const LEFT_CLEAR_GAP = STACK_GAP; // 13.333 authored
const wordmarkHalfClearance = () => `calc(${WORDMARK.widthPct / 2}vw + ${u(LEFT_CLEAR_GAP)})`;

/** `panelWidth()` for a panel anchored at the LEFT rail inset: never wider than the room right of that anchor up to the centred wordmark. */
export const panelWidthClearOfWordmark = () =>
  `min(${panelWidth()}, max(calc(50vw - ${RAIL_LEFT_PCT}vw - ${wordmarkHalfClearance()}), ${u(PANEL_MIN_W)}))`;

/** The same value in JS, for tests and arithmetic. @param {number} viewportWidth @param {number} unit resolved `--u`, in real px */
export function panelWidthClearOfWordmarkFor(viewportWidth, unit) {
  const half = (WORDMARK.widthPct / 100) * viewportWidth / 2;
  const room = viewportWidth / 2 - (RAIL_LEFT_PCT / 100) * viewportWidth - half - LEFT_CLEAR_GAP * unit;
  return Math.min(panelWidthFor(viewportWidth, unit), Math.max(room, PANEL_MIN_W * unit));
}

/** A panel's content column for a given CSS panel width (padding off both sides). `undefined` = today's fixed authored column. */
export const panelContentWidth = (widthCss) => (
  widthCss ? `calc(${widthCss} - ${u(PANEL_PAD * 2)})` : u(PANEL_CONTENT_W)
);
